// Panel embebido en BotPage que lista las sesiones activas del bot y
// ofrece un boton "Resetear" por fila. Resetar = `markExpired` server-side
// (el siguiente mensaje del usuario crea una sesion nueva desde el entry
// flow). Polea cada 10s.

import { Loader2, RotateCcw, Users } from 'lucide-react';
import { Can } from '@/components/auth/Can';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useActiveSessions, useResetSession } from '@/hooks/queries/use-active-sessions';
import { formatRelative } from '@/lib/relative-time';

interface Props {
  botId: number;
}

export function ActiveSessionsPanel({ botId }: Props): React.ReactElement {
  const query = useActiveSessions(botId);
  const reset = useResetSession(botId);

  function handleReset(sessionId: number): void {
    if (
      typeof window !== 'undefined' &&
      !window.confirm(
        'Expirar esta sesion forzara al usuario a empezar desde el entry flow al siguiente mensaje. ¿Continuar?',
      )
    ) {
      return;
    }
    reset.mutate(sessionId);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-4 w-4" aria-hidden />
          Sesiones activas
          {query.data?.sessions.length != null && (
            <span className="rounded bg-muted px-2 py-0.5 text-xs font-normal text-muted-foreground">
              {query.data.sessions.length}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {query.isLoading && <p className="text-sm text-muted-foreground">Cargando sesiones...</p>}
        {query.isError && (
          <p className="text-sm text-destructive">
            No se pudieron cargar las sesiones activas.{' '}
            <button
              type="button"
              onClick={() => query.refetch()}
              className="underline underline-offset-2"
            >
              Reintentar
            </button>
          </p>
        )}
        {query.data?.partial && (
          <p className="text-sm text-muted-foreground">
            La gestion de sesiones no esta cableada en este deployment.
          </p>
        )}
        {query.data && !query.data.partial && query.data.sessions.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No hay sesiones activas. Cuando un usuario inicie una conversacion aparecera aqui.
          </p>
        )}
        {query.data && !query.data.partial && query.data.sessions.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-2">Usuario</th>
                  <th className="py-2 pr-2">Flow</th>
                  <th className="py-2 pr-2">Step</th>
                  <th className="py-2 pr-2">Ultimo msg</th>
                  <th className="py-2 pr-2">Expira</th>
                  <th className="py-2 pr-2 text-right">Accion</th>
                </tr>
              </thead>
              <tbody>
                {query.data.sessions.map((s) => {
                  const isResetting = reset.isPending && reset.variables === s.id;
                  return (
                    <tr key={s.id} className="border-b border-border/50">
                      <td className="py-2 pr-2">
                        <div className="font-mono text-xs">{s.userPhone}</div>
                        {s.userName && (
                          <div className="text-xs text-muted-foreground">{s.userName}</div>
                        )}
                      </td>
                      <td className="py-2 pr-2 font-mono text-xs">{s.flowId ?? '—'}</td>
                      <td className="py-2 pr-2 font-mono text-xs">
                        {s.stepIndex != null ? `#${s.stepIndex}` : '—'}
                      </td>
                      <td className="py-2 pr-2 text-xs text-muted-foreground">
                        {formatRelative(s.updatedAt)}
                      </td>
                      <td className="py-2 pr-2 text-xs text-muted-foreground">
                        {formatRelative(s.expiresAt)}
                      </td>
                      <td className="py-2 pr-2 text-right">
                        <Can capability="edit_bot" botId={botId}>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReset(s.id)}
                            disabled={isResetting}
                            aria-label={`Resetear sesion del usuario ${s.userPhone}`}
                            title="Expirar sesion para que arranque desde el entry flow"
                          >
                            {isResetting ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <RotateCcw className="h-3.5 w-3.5" />
                            )}
                            <span>Resetear</span>
                          </Button>
                        </Can>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
